import { useMemo, type ReactElement } from "react";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  LayoutGrid,
  Plus,
  X,
} from "lucide-react";
import {
  AsyncSection,
  asyncStatus,
  Alert,
  Button,
  Card,
  EmptyState,
  SectionHeader,
  Select,
  SettingRow,
  SettingsGroup,
  SettingsDetailsButton,
  SettingsMetaCard,
  SettingsPageLayout,
  SettingsSaveBar,
  Skeleton,
  SortableItem,
  SortableList,
  cn,
  type SortableItemRenderProps,
} from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import { useAuthStore } from "../../auth/auth-store.js";
import { getEnabledModules } from "../../modules/index.js";
import {
  SETTINGS_MODULES,
  SETTINGS_MODULE_BY_ID,
  launcherPreferenceIdOf,
  normalizeLauncherPreferenceId,
  type SettingsModule,
} from "../../settings/roster.js";
import {
  LANDING_OPTIONS,
  LAUNCHER_DEFAULTS,
  type LandingOption,
  type LauncherPrefs,
} from "../../settings/types.js";
import { useSettingsDraft } from "../../settings/use-settings-draft.js";
import {
  useLauncherPreferencesQuery,
  useUpdateLauncherPreferences,
} from "../../settings/useSettings.js";

/* Launcher (PB-5134, PB-5313) on the LIVE launcher preference. The persisted
 * `favourites` wire field predates the module-roster UX; it now carries the
 * ordered module ids shown in the launcher. Keeping that field preserves the
 * API/database compatibility while the page presents the real user job: add,
 * remove, and reorder launcher modules. Save remains one compare-and-swap
 * replacement through the serialized writer. Unknown or workspace-disabled
 * ids stay preserved during unrelated edits. */

/** Weave hidden module ids back into their original slots so an app that is
 * re-enabled keeps its rank. Visible modules follow the editor's order; a
 * removed visible module collapses its slot, and newly added modules follow the
 * last preserved slot. The name mirrors the legacy API field. */
function composeFavourites(
  serverOrder: readonly string[],
  visible: readonly string[],
  hidden: ReadonlySet<string>,
): string[] {
  const merged: string[] = [];
  let next = 0;
  for (const id of serverOrder) {
    if (hidden.has(id)) {
      merged.push(id);
    } else if (next < visible.length) {
      const value = visible[next];
      next += 1;
      if (value !== undefined) merged.push(value);
    }
  }
  for (; next < visible.length; next += 1) {
    const value = visible[next];
    if (value !== undefined) merged.push(value);
  }
  return merged;
}

function initialsOf(name: string): string {
  return name
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(0, 2)
    .toUpperCase();
}

export function LauncherSettings(): ReactElement {
  const launcherQuery = useLauncherPreferencesQuery();
  const launcher = launcherQuery.data;
  const claims = useAuthStore((state) => state.claims);
  const enabledIds = useMemo(
    () => new Set(getEnabledModules(claims?.enabledModules).map((m) => m.id)),
    [claims?.enabledModules],
  );
  const enabledApps = useMemo(
    () =>
      SETTINGS_MODULES.filter((app) =>
        enabledIds.has((app.authModuleKey ?? app.id) as never),
      ),
    [enabledIds],
  );
  const landingLabel = (value: string): string =>
    LANDING_OPTIONS.find((option) => option.value === value)?.label ?? value;

  const meta = (
    <SettingsMetaCard
      icon={LayoutGrid}
      title="About the launcher"
      description="Your launcher is personal. Choose which workspace modules appear and the order they use."
      rows={[
        {
          label: "Modules shown",
          value: launcher
            ? visibleLauncherModuleIds(launcher, enabledApps).length
            : "—",
        },
        {
          label: "Available modules",
          value: enabledApps.length,
        },
        {
          label: "Opens on",
          value: launcher ? landingLabel(launcher.defaultLanding) : "—",
        },
      ]}
    />
  );

  return (
    <div className="pb-motion-content-enter">
      <SectionHeader
        variant="settings"
        leading={<LayoutGrid className="w-4 h-4 text-pb-cyan-text" />}
        title="Launcher"
        description={`Choose which modules appear in your launcher, arrange their order, and set where ${PRODUCT_NAME} opens.`}
        actionsSlot={<SettingsDetailsButton meta={meta} />}
      />
      <SettingsPageLayout meta={meta} mainWidth="fluid">
        {/* Four states, not two. This branched on `!launcher`, which has no
            terminal state: a failed query leaves `data` undefined and the
            skeleton on screen for ever, and the launcher's preferences are
            durable so that failure is reachable today. */}
        <AsyncSection
          status={asyncStatus({
            isLoading: launcherQuery.isLoading,
            isError: launcherQuery.isError,
            isEmpty: false,
            hasData: launcher !== undefined,
          })}
          loadingLabel="your launcher settings"
          errorTitle="Couldn't load your launcher settings"
          errorDescription={launcherQuery.error?.message}
          onRetry={() => void launcherQuery.refetch()}
          skeleton={
            <div className="flex flex-col gap-3.5">
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-10" />
              <Skeleton className="h-16" />
            </div>
          }
        >
          {launcher ? (
            <LauncherForm launcher={launcher} enabledApps={enabledApps} />
          ) : null}
        </AsyncSection>
      </SettingsPageLayout>
    </div>
  );
}

function LauncherForm({
  launcher,
  enabledApps,
}: {
  readonly launcher: LauncherPrefs;
  readonly enabledApps: readonly SettingsModule[];
}): ReactElement {
  const { update, isPending, error } = useUpdateLauncherPreferences();
  const enabledFavIds = useMemo(
    () => new Set(enabledApps.map(launcherPreferenceIdOf)),
    [enabledApps],
  );
  const moduleIds = useMemo(
    () => visibleLauncherModuleIds(launcher, enabledApps),
    [launcher, enabledApps],
  );
  // Module curation is an immediate action. The only draft on this page is the
  // default landing destination, which remains an explicit Save workflow.
  const savedLanding = useMemo(
    () => ({ defaultLanding: launcher.defaultLanding }),
    [launcher.defaultLanding],
  );
  const form = useSettingsDraft(savedLanding);
  const { draft, completeSave, failSave } = form;

  const setLauncherModules = (
    favourites: readonly string[],
    preserveHidden = true,
  ): void => {
    const next = [...favourites];
    void update((current) => {
      const canonicalCurrent = current.favourites.map(
        normalizeLauncherPreferenceId,
      );
      return {
        favourites: preserveHidden
          ? composeFavourites(
              canonicalCurrent,
              next,
              new Set(canonicalCurrent.filter((id) => !enabledFavIds.has(id))),
            )
          : next,
        moduleRosterConfigured: true,
        showMoreApps: current.showMoreApps,
        defaultLanding: current.defaultLanding,
      };
    });
  };
  const moveModule = (id: string, direction: -1 | 1): void => {
    const favourites = [...moduleIds];
    const index = favourites.indexOf(id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= favourites.length) return;
    const moved = favourites[index];
    const swapped = favourites[target];
    if (moved === undefined || swapped === undefined) return;
    favourites[index] = swapped;
    favourites[target] = moved;
    setLauncherModules(favourites);
  };
  const addModule = (id: string): void => {
    if (!moduleIds.includes(id)) setLauncherModules([...moduleIds, id]);
  };
  const removeModule = (id: string): void =>
    setLauncherModules(moduleIds.filter((moduleId) => moduleId !== id));

  const available = enabledApps.filter(
    (app) => !moduleIds.includes(launcherPreferenceIdOf(app)),
  );
  // Landing: Home, the launcher, and the modules enabled for this person; a
  // saved value the list lacks stays selectable so nothing is silently moved.
  const landingOptions: LandingOption[] = [
    ...LANDING_OPTIONS.filter(
      (option) =>
        option.value === "home" ||
        option.value === "launcher" ||
        enabledApps.some((app) => app.id === option.value),
    ),
  ];
  if (!landingOptions.some((option) => option.value === draft.defaultLanding)) {
    landingOptions.push({
      value: draft.defaultLanding,
      label:
        LANDING_OPTIONS.find((option) => option.value === draft.defaultLanding)
          ?.label ?? draft.defaultLanding,
    });
  }

  return (
    <div>
      {error ? (
        <Alert
          tone="critical"
          title="Your last launcher change could not be saved"
          className="mb-5"
        >
          The page now shows the latest saved preferences; apply the change
          again.
        </Alert>
      ) : null}

      <SettingsGroup
        variant="settings"
        title="Modules in launcher"
        description="Add or remove modules, then drag the handle or use the arrows to set their launcher order. Changes save automatically."
        actionsSlot={
          // eslint-disable-next-line ds/no-hand-built -- kept: Button link is px-0 with no hover wash and ghost is secondary text; this padded cyan text action has no PR 3 look row.
          <button
            type="button"
            onClick={() =>
              setLauncherModules(enabledApps.map(launcherPreferenceIdOf), false)
            }
            disabled={
              moduleIds.length === enabledApps.length &&
              moduleIds.every(
                (id, index) =>
                  id ===
                  (enabledApps[index]
                    ? launcherPreferenceIdOf(enabledApps[index])
                    : undefined),
              )
            }
            className="rounded-md px-2 py-1 text-body-small font-medium text-pb-accent-text transition-colors hover:pb-overlay-paint active:pb-overlay-paint-strong focus-ring disabled:cursor-not-allowed disabled:opacity-40"
          >
            Restore all modules
          </button>
        }
      >
        <p
          aria-live="polite"
          className="mt-1 min-h-4 text-caption text-pb-text-muted"
        >
          {isPending ? "Saving module changes…" : ""}
        </p>
        {moduleIds.length === 0 ? (
          /* The framed empty state: EmptyState on the dashed Card. */
          <Card variant="dashed" className="mt-3">
            <EmptyState
              icon={<LayoutGrid className="h-5 w-5 text-pb-text-muted" />}
              title="No modules in your launcher"
              description="Add a workspace module below to make it available from the launcher."
            />
          </Card>
        ) : (
          <SortableList ids={[...moduleIds]} onReorder={setLauncherModules}>
            {/* Kept a plain list: the rows are sortable items with a drag
                handle, and List would restyle them. */}
            <ul aria-label="Launcher modules" className="mt-1 flex flex-col">
              {moduleIds.map((id, index) => {
                const app = SETTINGS_MODULE_BY_ID[id];
                if (!app) return null;
                return (
                  <SortableItem key={id} id={id}>
                    {({
                      setNodeRef,
                      style,
                      handleProps,
                      isDragging,
                    }: SortableItemRenderProps) => (
                      <li
                        ref={setNodeRef}
                        style={style}
                        className={cn(
                          "flex items-center gap-3 border-b border-pb-border/50 py-2.5",
                          isDragging &&
                            "rounded-md bg-pb-card-elevated shadow-[var(--pbds-shadow-float)]",
                        )}
                      >
                        {/* eslint-disable-next-line ds/no-hand-built -- kept: a drag handle (dnd-kit listeners on a bare glyph), a keep case in the plan. */}
                        <button
                          {...handleProps}
                          type="button"
                          aria-label={`Drag to reorder ${app.name}`}
                          className="flex shrink-0 cursor-grab touch-none rounded-sm text-pb-text-muted focus-ring"
                        >
                          <GripVertical className="h-4 w-4" aria-hidden />
                        </button>
                        {/* Kept: Avatar has no 30px step and no module
                            tile paint. */}
                        <span
                          aria-hidden
                          className={cn(
                            "flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-md text-dense-1 font-bold text-pb-on-accent",
                            app.tile,
                          )}
                        >
                          {initialsOf(app.name)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-body font-medium text-pb-text">
                            {app.name}
                          </div>
                          <div className="mt-px truncate text-caption text-pb-text-muted">
                            {app.short}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-0.5">
                          {/* eslint-disable-next-line ds/no-hand-built -- kept: Button ghost iconOnly sm is this 28px box, but it paints the glyph text-pb-text-secondary at rest instead of today's muted and dims to 50% when disabled (today 30%); no PR 3 look row approves that. */}
                          <button
                            type="button"
                            aria-label={`Move ${app.name} up`}
                            disabled={index === 0}
                            onClick={() => moveModule(id, -1)}
                            className={MODULE_ACTION_BUTTON}
                          >
                            <ChevronUp className="h-4 w-4" aria-hidden />
                          </button>
                          {/* eslint-disable-next-line ds/no-hand-built -- kept: Button ghost iconOnly sm is this 28px box, but it paints the glyph text-pb-text-secondary at rest instead of today's muted and dims to 50% when disabled (today 30%); no PR 3 look row approves that. */}
                          <button
                            type="button"
                            aria-label={`Move ${app.name} down`}
                            disabled={index === moduleIds.length - 1}
                            onClick={() => moveModule(id, 1)}
                            className={MODULE_ACTION_BUTTON}
                          >
                            <ChevronDown className="h-4 w-4" aria-hidden />
                          </button>
                          {/* eslint-disable-next-line ds/no-hand-built -- kept: Button ghost iconOnly sm is this 28px box, but it paints the glyph text-pb-text-secondary at rest instead of today's muted and dims to 50% when disabled (today 30%); no PR 3 look row approves that. */}
                          <button
                            type="button"
                            aria-label={`Remove ${app.name} from launcher`}
                            onClick={() => removeModule(id)}
                            className={cn(
                              MODULE_ACTION_BUTTON,
                              "hover:bg-pb-red-soft hover:text-pb-red-text",
                            )}
                          >
                            <X className="h-4 w-4" aria-hidden />
                          </button>
                        </div>
                      </li>
                    )}
                  </SortableItem>
                );
              })}
            </ul>
          </SortableList>
        )}
      </SettingsGroup>

      <SettingsGroup
        variant="settings"
        title="Add modules"
        description="Add any module your admin has made available."
      >
        {available.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {/* The dashed add row is Button variant="dashed" (OK-06). */}
            {available.map((app) => (
              <Button
                key={app.id}
                variant="dashed"
                aria-label={`Add ${app.name} to launcher`}
                onClick={() => addModule(launcherPreferenceIdOf(app))}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                {app.name}
              </Button>
            ))}
          </div>
        ) : (
          /* Kept as today's 13px line: EmptyState size="sm" sets 11px type,
             a look change no PR 3 row approves. */
          <p className="mt-2 text-body-small text-pb-text-muted">
            Every available module is already in your launcher.
          </p>
        )}
      </SettingsGroup>

      <SettingsGroup variant="settings" title="Opening">
        <SettingRow
          label="Default landing page"
          description={`Where ${PRODUCT_NAME} opens when you sign in.`}
          control={
            <div className="sm:w-48">
              <Select
                ariaLabel="Default landing page"
                options={landingOptions}
                value={draft.defaultLanding}
                onChange={(defaultLanding) =>
                  form.setDraft((current) => ({ ...current, defaultLanding }))
                }
              />
            </div>
          }
        />
      </SettingsGroup>

      <SettingsSaveBar
        state={form.state}
        savedHint="Saved to your account"
        unsavedHint="Unsaved changes"
        resetLabel="Reset landing"
        saveLabel="Save landing"
        onReset={() =>
          form.setDraft({
            defaultLanding: LAUNCHER_DEFAULTS.defaultLanding,
          })
        }
        onSave={() => {
          form.beginSave();
          void update((current) => ({
            favourites: current.favourites,
            moduleRosterConfigured: current.moduleRosterConfigured,
            showMoreApps: current.showMoreApps,
            defaultLanding: draft.defaultLanding,
          })).then((saved) => {
            if (saved) {
              completeSave();
            } else {
              failSave();
            }
          });
        }}
      />
    </div>
  );
}

function visibleLauncherModuleIds(
  launcher: LauncherPrefs,
  enabledApps: readonly SettingsModule[],
): string[] {
  // The explicit marker separates an intentionally empty roster from legacy
  // preferences whose row/version changed only for landing or discovery.
  if (!launcher.moduleRosterConfigured) {
    return enabledApps.map(launcherPreferenceIdOf);
  }
  const enabledIds = new Set(enabledApps.map(launcherPreferenceIdOf));
  return launcher.favourites
    .map(normalizeLauncherPreferenceId)
    .filter(
      (id, index, ids) => enabledIds.has(id) && ids.indexOf(id) === index,
    );
}

const MODULE_ACTION_BUTTON =
  "flex h-7 w-7 items-center justify-center rounded-md text-pb-text-muted transition-colors hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong focus-ring disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-none";
